<template>
  <NetworkStatus v-if="isOffline" />
  <div v-else-if="isLoading" class="loading-container">
    <div class="loading-content">
      <img class="logo-img mb-4" src="@/assets/img/logo-ct.png" alt="Logo" />
      <h3 class="text-white font-weight-bolder mb-4">Scolarfleet</h3>
      <div class="progress-container">
        <div class="progress">
          <div class="progress-bar bg-success" role="progressbar" :style="{ width: `${loadingProgress}%` }"
               :aria-valuenow="loadingProgress" aria-valuemin="0" aria-valuemax="100">
            {{ loadingProgress }}%
          </div>
        </div>
        <p class="text-white mt-2">{{ $t("login.charging") }}</p>
      </div>
    </div>
  </div>
  <div v-else>
    <main class="mt-0 main-content min-vh-100">
      <section>
        <div class="container-fluid">
          <div class="row">
            <div class="col-12 col-lg-6 py-0 text-center min-vh-100">
              <div class="auth-wrapper mx-auto w-100">
                <div class="logo-title-container mt-lg-7">
                  <div class="image-wrapper">

                  </div>
                  <div class="title-wrapper" style="transform: translateY(-60%)">
                    <img class="logo-img" style="width: 45px; margin-right: 10px" src="@/assets/img/logo-ct.png" alt="" />
                    <h3 class="text-white font-weight-bolder position-relative">
                      Scolarfleet
                    </h3>
                  </div>
                </div>

                <!-- Formulaire de connexion -->
                <transition name="fade-slide">
                  <div v-if="!showResetForm" class="card login-card mx-auto">
                    <div class="pb-0 card-header text-start">
                      <h4 class="font-weight-bolder">
                        {{ $t("login.title") }}
                      </h4>
                      <p class="mb-0">
                        {{ $t("login.instruction") }}
                      </p>
                    </div>

                    <div class="card-body">
                      <div v-if="loginError"
                           class="alert alert-danger text-white d-flex justify-content-between align-items-center"
                           role="alert">
                        <span>{{ loginError }}</span>
                        <button type="button" class="btn-close btn-close-white" aria-label="Close"
                                @click="loginError = ''"></button>
                      </div>

                      <!-- Tabs pour choisir le type de connexion -->
                      <div class="login-tabs mb-4">
                        <div class="tab-buttons d-flex">


                          <button type="button" class="tab-button flex-fill" :class="{ active: isPhoneLogin }"
                                  @click="switchTab('phone')">
                            <i class="bi bi-telephone-fill me-2"></i>
                            {{ $t("login.phone_tab") || "Téléphone" }}
                          </button>
                          <button type="button" class="tab-button flex-fill" :class="{ active: isUserNameLogin }"
                                  @click="switchTab('username')">
                            <i class="bi bi-person-fill me-2"></i>
                            {{ $t("login.username_tab") || "Nom d'utilisateur" }}
                          </button>

                        </div>
                      </div>

                      <form role="form">
                        <!-- Tab Username -->
                        <div v-if="isUserNameLogin" class="mb-3">
                          <label class="w-100 text-start d-flex" for="username">
                            {{ $t("login.label_username") }}
                            <span class="text-danger ms-1">*</span>
                          </label>
                          <div class="input-container position-relative">
                            <argon-input v-model="username" name="username"
                                         :placeholder="$t('login.placeholder_username')" size="lg" type="text"
                                         :class="{ 'is-invalid': usernameError }" @input="clearUsernameError" />
                            <span class="input-icon position-absolute top-50 translate-middle-y">
                              <i class="text-dark bi bi-person-fill"></i>
                            </span>
                          </div>
                          <small class="text-danger text-start" v-if="usernameError">{{ usernameError }}</small>
                        </div>

                        <!-- Tab Phone -->
                        <div v-if="isPhoneLogin" class="mb-3">
                          <label class="w-100 text-start d-flex" for="phone">
                            {{ $t("login.label_phone") || "Numéro de téléphone" }}
                            <span class="text-danger ms-1">*</span>
                          </label>

                          <argon-phone-input v-model="username" name="username" id="phone"
                                             :placeholder="$t('login.placeholder_phone') || '658959632'" size="lg"
                                             :valid="!phoneError && username.length === getExpectedPhoneLength(selectedCountryCode)"
                                             :class="{ 'is-invalid': phoneError }" :country-code="selectedCountryCode"
                                             :max-length="getExpectedPhoneLength(selectedCountryCode)" @input="handlePhoneInput"
                                             @focus="clearPhoneError" @country-changed="onCountryChanged" />

                          <small class="text-danger text-start" v-if="usernameError">{{ usernameError }}</small>
                        </div>
                        <div class="mb-3 position-relative">
                          <label class="w-100 text-start d-flex" for="password">
                            {{ $t("login.label_password") }}
                            <span class="text-danger ms-1">*</span>
                          </label>
                          <div class="password-input-container position-relative">
                            <argon-input v-model="password" name="password"
                                         :placeholder="$t('login.placeholder_password')" size="lg"
                                         :type="showPassword ? 'text' : 'password'" :class="{ 'is-invalid': passwordError }"
                                         @input="clearPasswordError" />
                            <span class="input-icon position-absolute top-50 translate-middle-y">
                              <i class="text-dark bi bi-lock-fill"></i>
                            </span>
                            <span class="password-toggle-icon position-absolute top-50 translate-middle-y"
                                  @click="togglePasswordVisibility">
                              <i v-if="showPassword" class="text-dark bi bi-eye-fill"></i>
                              <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                            </span>
                          </div>
                          <small class="text-danger text-start" v-if="passwordError">{{ passwordError }}</small>
                        </div>

                        <!-- Lien "Mot de passe oublié" -->
                        <div class="text-end mb-2">
                          <a href="#" style="font-size: 15px" class="" @click.prevent="toggleResetForm">
                            <span style="color: #75acff">
                              {{ $t("login.forgot_password") }}</span>
                          </a>
                        </div>

                        <div class="mb-3">
                          <argon-switch id="rememberMe">{{
                              $t("login.remember")
                            }}</argon-switch>
                        </div>

                        <div class="text-center">
                          <argon-button class="mt-4 w-100" color="success" size="lg" variant="gradient"
                                        :loading="loading" :active="buttonActive || showConfirmation" :clickEvent="button"
                                        @click="login">
                            {{ $t("button.sign_in") }}
                          </argon-button>
                        </div>
                      </form>
                    </div>
                  </div>
                </transition>

                <!-- Replace the existing reset password form with this updated version -->
                <transition name="fade-slide">
                  <div v-if="showResetForm" class="card login-card mx-auto">
                    <div class="pb-0 card-header text-start">
                      <h4 class="font-weight-bolder">
                        {{ $t("login.reset_password") }}
                      </h4>
                      <p class="mb-0">
                        {{
                          resetLinkSent
                              ? $t("login.reset_link_sent")
                              : $t("login.reset_instruction")
                        }}
                      </p>
                    </div>

                    <div class="card-body">
                      <div v-if="resetError"
                           class="alert alert-danger text-white d-flex justify-content-between align-items-center"
                           role="alert">
                        <span>{{ resetError }}</span>
                        <button type="button" class="btn-close btn-close-white" aria-label="Close"
                                @click="resetError = ''"></button>
                      </div>

                      <!-- Message de succès -->
                      <div v-if="resetLinkSent" class="alert bg-success text-white" role="alert"
                           style="padding-bottom: 4rem">
                        <div class="text-end text-light align-items-center mb-2">
                          <button type="button" class="btn-close btn-close-white" aria-label="Close"
                                  @click="resetLinkSent = false"></button>
                        </div>
                        <span class="text-white">{{
                            $t("login.reset_link_sent_message")
                          }}</span>
                        <br />
                        <span class="mb-0">
                          {{ $t("login.expires_in") }}:
                          {{ this.times_expired }} minutes
                        </span>
                      </div>

                      <!-- Step 1: Email input form -->
                      <form v-if="resetStep === 1 && !resetLinkSent" role="form">
                        <div class="mb-3">
                          <label class="w-100 text-start d-flex" for="phoneNumber">
                            {{ $t("login.label_phone") || "Numéro de téléphone" }}<span class="text-danger ms-1">*</span>
                          </label>
                          <div class="input-container position-relative">
                            <argon-phone-input
                              v-model="phoneNumber"
                              name="phoneNumber"
                              id="phoneNumber"
                              :placeholder="'Ex:'+ '658959632'"
                              size="lg"
                              :valid="!emailError && phoneNumber.length === getExpectedPhoneLength(selectedCountryCode)"
                              :class="{ 'is-invalid': emailError }"
                              @input="clearEmailError"
                              @focus="clearEmailError"
                              :country-code="selectedCountryCode"
                              :max-length="getExpectedPhoneLength(selectedCountryCode)"
                              @country-changed="onCountryChanged"
                            />
                            
                          </div>
                          <small class="text-danger text-start" v-if="emailError">{{ emailError }}</small>
                        </div>

                        <div class="d-flex justify-content-between mt-4">
                          <argon-button class="mt-4 w-100" color="success" size="lg" variant="gradient"
                                        :loading="resetLoading" :active="buttonActive" :clickEvent="button"
                                        @click="requestPasswordReset">
                            {{ $t("button.send_reset_link") }}
                          </argon-button>
                        </div>
                        <div class="text-start mb-4" style="margin-top: 50px; color: orange">
                          <i class="fa-solid fa-arrow-left"></i>
                          <a href="#" class="ms-3" @click.prevent="toggleResetForm">
                            <span style="color: orange">
                              {{ $t("button.sign_in") }}</span>
                          </a>
                        </div>
                      </form>

                      <!-- Bouton pour faire une nouvelle demande après expiration -->
                      <div v-if="resetLinkSent" class="mt-4">
                        <div class="text-start mb-4" style="margin-top: 20px">
                          <i class="fa-solid fa-arrow-left"></i>
                          <a href="#" class="ms-3" @click.prevent="toggleResetForm">
                            <span style="color: orange">
                              {{ $t("button.sign_in") }}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Bannière avec images rotatives -->
            <div class="col-lg-6 d-none d-lg-flex position-relative overflow-hidden">
              <div class="position-absolute h-100 w-100">
                <transition-group name="fade" tag="div" class="h-100 py-0">
                  <div v-for="(banner, index) in banner" :key="index" class="slider-image position-relative"
                       v-show="currentImageIndex === index" :style="{
                      backgroundImage: `url(${banner.image})`,
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                    }">
                    <span class="mask"></span>
                    <div v-if="banner.title && banner.description"
                         class="banner-content position-absolute bottom-0 text-white p-4">
                      <div class="title">
                        <h3>{{ banner.title }}</h3>
                        <p>{{ banner.description }}</p>
                      </div>
                    </div>
                  </div>
                </transition-group>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonPhoneInput from "@/components/ArgonPhoneInput.vue";
import NetworkStatus from "@/views/components/NetworkStatus.vue";
import { getbanners, getsystem_config } from "@/utils/services";
import { loginUser, ResetPassword } from "@/utils/auth";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import api_config from "@/config";
import axios from 'axios'

export default {
  name: "xschool-signin",
  components: {
    ArgonInput,
    ArgonPhoneInput,
    ArgonSwitch,
    ArgonButton,
    NetworkStatus,
  },
  data() {
    return {
      button: "",
      loginError: "",
      username: "",
      usernameError: "",
      phoneNumber: "",
      password: "",
      passwordError: "",
      confirmPasswordError: "",
      failedLogin: "",
      successLogin: "",
      loading: false,
      buttonActive: false,
      showConfirmation: false,
      showPassword: false,
      isOffline: false,
      phoneError: "",
      formTouched: false,
      currentImageIndex: 0,
      slideInterval: null,
      banner: [],
      phone_prefix: api_config.PHONE_PREFIX,
      showResetForm: false,
      // Removed unused 'email' and 'phone' data properties to avoid confusion
      emailError: "",
      resetError: "",
      resetSuccess: "",
      resetLoading: false,
      resetStep: 1,
      verificationCode: "",
      newPassword: "",
      confirmPassword: "",
      codeError: "",
      newPasswordError: "",
      showNewPassword: false,
      showConfirmPassword: false,
      resetPasswordLoading: false,
      times_expired: 0,
      resetLinkSent: false,
      expirationTime: null,
      remainingTime: 0,
      timerInterval: null,
      ip_toolbox_api_url: localStorage.getItem("ip_toolbox_api_url"),
      isLoading: true,
      loadingProgress: 0,
      totalImages: 0,
      loadedImages: 0,
      isUserNameLogin: false,
      isPhoneLogin: true,
      rememberMe: false,
      selectedCountryCode: localStorage.getItem("callingCode"), // Code par défaut
      selectedCountry: null,
    };
  },

  async mounted() {
    await this.get_app_config();
    await this.loadBanners();
    await window.addEventListener("online", this.updateOnlineStatus);
    await window.addEventListener("offline", this.updateOnlineStatus);
    await this.startSlideshow();
  },

  async created() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showNavbar = false;
    this.$store.state.showSidenav = false;
    this.$store.state.showFooter = false;
    document.body.classList.remove("bg-gray-100");

    // Charger les préférences de pays sauvegardées
    const savedCountryCode = localStorage.getItem('selectedCountryCode');
    const savedCountry = localStorage.getItem('selectedCountry');

    if (savedCountryCode) {
      this.selectedCountryCode = savedCountryCode;
      this.phone_prefix = `+${savedCountryCode}`;
    }

    if (savedCountry) {
      this.selectedCountry = JSON.parse(savedCountry);
    }

    const rememberedUsername = localStorage.getItem("rememberedUsername");
    const rememberedPhone = localStorage.getItem("rememberedPhone");
    const rememberedTab = localStorage.getItem("rememberedTab");
    const hasRememberedCredentials = localStorage.getItem("hasRememberedCredentials");

    if (hasRememberedCredentials) {
      if (rememberedTab === 'username' && rememberedUsername && rememberedUsername.trim()) {
        this.activeTab = 'username';
        this.username = rememberedUsername;
        this.phoneNumber = ''; // S'assurer que le champ phone est vide
      } else if (rememberedTab === 'phone' && rememberedPhone && rememberedPhone.trim()) {
        this.activeTab = 'phone';
        this.phoneNumber = rememberedPhone;
        this.username = ''; // S'assurer que le champ username est vide
      }
      this.rememberMe = true;

      this.$nextTick(() => {
        const passwordInput = document.querySelector('input[name="password"]');
        if (passwordInput) passwordInput.focus();
      });
    } else {
      // Valeurs par défaut si aucune credential mémorisée
      this.activeTab = 'phone';
      this.username = '';
      this.phoneNumber = '';
    }
  },

  async beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showNavbar = true;
    this.$store.state.showSidenav = true;
    this.$store.state.showFooter = true;
    document.body.classList.add("bg-gray-100");
    window.removeEventListener("online", this.updateOnlineStatus);
    window.removeEventListener("offline", this.updateOnlineStatus);
    this.stopSlideshow();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },

  computed: {
    formatRemainingTime() {
      const minutes = Math.floor(this.remainingTime / 60);
      const seconds = this.remainingTime % 60;
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    },

    progressPercentage() {
      if (!this.expirationTime || !this.times_expired) return 100;
      const totalTime = this.times_expired * 60; // en secondes
      return (this.remainingTime / totalTime) * 100;
    },
  },

  methods: {
    // === GESTION DES ONGLETS ===
    switchTab(tab) {
      if (tab === 'phone') {
        this.isPhoneLogin = true;
        this.isUserNameLogin = false;
      } else if (tab === 'username') {
        this.isPhoneLogin = false;
        this.isUserNameLogin = true;
      }
      this.clearAllErrors();

      // Nettoyer les champs non utilisés
      if (this.isPhoneLogin) {
        this.username = '';
      } else {
        this.phoneNumber = '';
      }
    },

    // === GESTION DES ERREURS ===
    clearAllErrors() {
      this.usernameError = "";
      this.phoneError = "";
      this.passwordError = "";
      this.loginError = "";
    },

    clearPhoneError() {
      const expectedLength = this.getExpectedPhoneLength(this.selectedCountryCode);
      if (this.phoneNumber.trim() && this.phoneNumber.length >= expectedLength) {
        this.phoneError = "";
      }
    },

    clearUsernameError() {
      if (this.username.trim()) {
        this.usernameError = "";
      }
    },

    clearPasswordError() {
      if (this.password) {
        this.passwordError = "";
      }
    },

    // === GESTION DU TÉLÉPHONE ===
    handlePhoneInput(value) {
      console.log("Phone input value:", value);
      // Convert value to string safely before calling replace
      const stringValue = value != null ? String(value) : '';
      // Filtrer pour ne garder que les chiffres
      const numbersOnly = stringValue.replace(/\D/g, '');

      // Limiter selon la longueur attendue pour le pays sélectionné
      const maxLength = this.getExpectedPhoneLength(this.selectedCountryCode);
      this.phoneNumber = numbersOnly.slice(0, maxLength);

      // Effacer l'erreur si le champ est valide
      this.clearPhoneError();
    },

    restrictToNumbers(event) {
      const char = String.fromCharCode(event.which);
      if (!/[0-9]/.test(char)) {
        event.preventDefault();
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return '';

      let cleanPhone = phone.replace(/\D/g, '');

      // Supprimer le code pays s'il est présent
      if (cleanPhone.startsWith(this.selectedCountryCode)) {
        cleanPhone = cleanPhone.substring(this.selectedCountryCode.length);
      }

      // Validation de la longueur selon le pays
      const expectedLength = this.getExpectedPhoneLength(this.selectedCountryCode);
      if (cleanPhone.length !== expectedLength) {
        // Retourner le numéro tel quel au lieu de lancer une exception
        return `+${this.selectedCountryCode}${cleanPhone}`;
      }

      return `+${this.selectedCountryCode}${cleanPhone}`;
    },

    // === GESTION DU CODE PAYS ===
    onCountryChanged(countryData) {
      this.selectedCountryCode = countryData.code;
      this.phone_prefix = countryData.fullCode;
      this.selectedCountry = countryData.country;

      // Réinitialiser le numéro de téléphone si nécessaire
      if (this.phoneNumber && this.phoneNumber.length > this.getExpectedPhoneLength(countryData.code)) {
        this.phoneNumber = this.phoneNumber.slice(0, this.getExpectedPhoneLength(countryData.code));
      }

      // Sauvegarder le choix de l'utilisateur
      localStorage.setItem('selectedCountryCode', countryData.code);
      localStorage.setItem('selectedCountry', JSON.stringify(countryData.country));

      // Effacer les erreurs
      this.clearPhoneError();
    },

    getExpectedPhoneLength(countryCode) {
      const lengthMap = {
        '237': 9,  // Cameroun
        '33': 9,   // France
        '1': 10,   // USA/Canada
        '44': 10,  // UK
        '49': 11,  // Allemagne
        '39': 10,  // Italie
        '34': 9,   // Espagne
        '41': 9,   // Suisse
        '32': 9,   // Belgique
        '31': 9,   // Pays-Bas
        '221': 9,  // Sénégal
        '225': 8,  // Côte d'Ivoire
        '241': 8,  // Gabon
        '235': 8,  // Tchad
        '236': 8,  // République centrafricaine
        '240': 9,  // Guinée équatoriale
        '242': 9,  // Congo
        '243': 9,  // RD Congo
        '212': 9,  // Maroc
        '213': 9,  // Algérie
        '216': 8,  // Tunisie
        '20': 10,  // Égypte
        '27': 9,   // Afrique du Sud
        '234': 10, // Nigeria
        '233': 9,  // Ghana
        '254': 9,  // Kenya
        '251': 9,  // Éthiopie
        '86': 11,  // Chine
        '81': 10,  // Japon
        '91': 10,  // Inde
        '55': 11,  // Brésil
        '61': 9,   // Australie
      };

      return lengthMap[countryCode] || 9; // 9 par défaut
    },

    async initializeCountryCode() {
      try {
        const r = await axios(`${this.ip_toolbox_api_url}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const response = r.data
        console.log("Réponse de l'API toolbox:", response);
        const savedCountryCode = response.country_code2.toLocaleLowerCase()
        localStorage.setItem("currencyCode", response.currency.code);
        localStorage.setItem("savedCountryCode", savedCountryCode);
        this.selectedCountryCode = response['calling_code'];
        this.phone_prefix = response['calling_code'];
        if (response['calling_code']) {
          const callingCode = response['calling_code'];
          const formattedCallingCode = callingCode.replace(/^\+/, "");
          localStorage.setItem("callingCode", formattedCallingCode);
          localStorage.setItem("selectedCountryCode", formattedCallingCode);
        }
      } catch (apiError) {
        console.error(
            this.$t('errors.api_toolbox_request'),
            apiError
        );
      }
      try {
        if (this.selectedCountryCode) {
          this.selectedCountryCode = this.selectedCountryCode.replace(/^\+/, '');
          const countryCodes = await import('@/assets/country-codes.json');
          this.selectedCountry = countryCodes.default.find(country =>
              country.code === this.selectedCountryCode
          ) || countryCodes.default[0];
        }
      } catch (error) {
        this.selectedCountryCode = '237';
        this.phone_prefix = '+237';
        const countryCodes = await import('@/assets/country-codes.json');
        this.selectedCountry = countryCodes.default.find(country =>
            country.code === '237'
        ) || countryCodes.default[0];
      }
    },

    // === VALIDATION DU FORMULAIRE ===
    validateForm() {
      let isValid = true;

      // Validation du nom d'utilisateur
      if (!this.username.trim()) {
        this.usernameError = this.$t("login.username_required");
        if (this.isPhoneLogin && !this.phoneNumber.trim()) {
          this.usernameError = this.$t("login.phone_required");
        } else if (this.isUserNameLogin && !this.username.trim()) {
          this.usernameError = this.$t("login.username_required");
        } else if (this.isPhoneLogin && this.phoneNumber.trim() && this.phoneNumber.length < this.getExpectedPhoneLength(this.selectedCountryCode)) {
          this.usernameError = this.$t("login.phone_invalid_length", { length: this.getExpectedPhoneLength(this.selectedCountryCode) });
        } else if (this.isUserNameLogin && this.username.trim() && this.username.length < 3) {
          this.usernameError = this.$t("login.username_invalid_length");
        }
        isValid = false;
      } else {
        this.usernameError = "";
      }

      // Validation du mot de passe
      if (!this.password) {
        this.passwordError = this.$t("login.password_required");
        isValid = false;
      } else {
        this.passwordError = "";
      }

      return isValid;
    },

    // === GESTION DES MOTS DE PASSE ===
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    // === SAUVEGARDE DES CREDENTIALS ===
    saveCredentials() {
      if (this.rememberMe) {
        if (this.activeTab === 'username' && this.username.trim()) {
          localStorage.setItem("rememberedUsername", this.username);
          localStorage.removeItem("rememberedPhone"); //
        } else if (this.activeTab === 'phone' && this.phoneNumber.trim()) {
          localStorage.setItem("rememberedPhone", this.phoneNumber);
          localStorage.removeItem("rememberedUsername"); // Nettoyer l'autre
        }
        localStorage.setItem("hasRememberedCredentials", "true");
        localStorage.setItem("rememberedTab", this.activeTab);
      } else {
        // Nettoyer toutes les données mémorisées
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPhone");
        localStorage.removeItem("hasRememberedCredentials");
        localStorage.removeItem("rememberedTab");
      }
    },

    // === GESTION DE LA CONNEXION ===

    async login() {
      if (this.loading) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }
      if (this.isPhoneLogin && this.username.trim()) {
        this.username = `${this.selectedCountryCode}${this.username.trim()}`;
      }

      this.loading = true;
      this.button = "disabled";
      this.loginError = "";

      try {
        let data = await loginUser(this.username, this.password);
        if (data.status === 200) {
          this.saveCredentials();
          localStorage.setItem("username", this.username);

          if (!this.showConfirmation) {
            this.showConfirmation = true;
            const message = this.$t("signin.login200");
            toast.success(message, {
              autoClose: 3000,
            });
          }
          // Load full app config data to localStorage before redirect
          const xpay_api_url = localStorage.getItem("xpay_api_url");
          const xpay_api_token = localStorage.getItem("xpay_api_token");
          const sf_web_api_url = localStorage.getItem("sf_web_api_url");
          const eska_api_url = localStorage.getItem("eska_api_url");
          const name_school = localStorage.getItem("name_school");
          const schoolId = localStorage.getItem("schoolId");
          const training_type = localStorage.getItem("training_type");
          const education_grade = localStorage.getItem("education_grade");
          const education_system = localStorage.getItem("education_system");
          if (xpay_api_url && xpay_api_token && sf_web_api_url && eska_api_url && name_school && schoolId && training_type && education_grade && education_system) {
            setTimeout(() => {
              this.$router.push({ name: "welcome" });
            }, 2000);
          } else {
            this.$router.push({ name: "Parem" });
          }
        } else if (data.status === 403) {
        this.loginError = data.message ||                 this.$t("login.blocked_account");
        localStorage.removeItem("authToken");
      } else {
        this.loginError = this.$t("login.invalid_credentials");
      }

      } catch (err) {
        this.isOffline = true;
      } finally {
        this.loading = false;
        this.button = "";
      }
    },

    async get_app_config() {
      try {
        const config = await getsystem_config();
        if (config && config.data.ip_toolbox_api_url) {
          this.ip_toolbox_api_url = config.data.ip_toolbox_api_url;
          localStorage.setItem("ip_toolbox_api_url", config.data.ip_toolbox_api_url);
          await this.initializeCountryCode();
        }
      } catch (error) {
        console.error(
            this.$t('errors.fetch_system_configs'),
            error
        );
      }
    },

    // === GESTION DES BANNIÈRES ===
    nextImage() {
      if (this.banner && this.banner.length > 0) {
        this.currentImageIndex =
            (this.currentImageIndex + 1) % this.banner.length;
      }
    },

    startSlideshow() {
      if (this.banner && this.banner.length > 0) {
        this.slideInterval = setInterval(this.nextImage, 10000);
      }
    },

    stopSlideshow() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval);
        this.slideInterval = null;
      }
    },

    clearBannersCache() {
      localStorage.removeItem("banners");
      localStorage.removeItem("bannersTimestamp");
    },

    async refreshBanners() {
      this.clearBannersCache();
      await this.loadBanners();
    },

    async loadBanners() {
      this.isLoading = true;
      this.loadingProgress = 0;

      try {
        const cachedBanners = localStorage.getItem("banners");
        const cachedTimestamp = localStorage.getItem("bannersTimestamp");
        const currentTime = new Date().getTime();
        const cacheExpiration = 5 * 60 * 1000;

        if (
            !cachedBanners ||
            !cachedTimestamp ||
            currentTime - parseInt(cachedTimestamp) > cacheExpiration
        ) {
          const browserLang = navigator.language.split("-")[0];
          const response = await getbanners(browserLang);

          if (response) {
            const activeBanners = response.filter((banner) => banner.is_active);
            this.banner = activeBanners;
            this.totalImages = activeBanners.length;

            // Précharger les images
            await this.preloadImages(activeBanners);

            // Sauvegarder en cache
            localStorage.setItem("banners", JSON.stringify(activeBanners));
            localStorage.setItem("bannersTimestamp", currentTime.toString());
          } else {
            this.loadingProgress = 100;
            this.banner = [];
          }
        } else {
          // Charger depuis le cache
          this.banner = JSON.parse(cachedBanners);
          this.totalImages = this.banner.length;
          await this.preloadImages(this.banner);
        }

        if (this.banner && this.banner.length > 0) {
          this.currentImageIndex = 0;
          this.stopSlideshow();
          this.startSlideshow();
        }
      } catch (error) {
        console.error("Error fetching data banners:", error);
        const cachedBanners = localStorage.getItem("banners");
        if (cachedBanners) {
          this.banner = JSON.parse(cachedBanners);
        } else {
          this.banner = [];
        }
        this.loadingProgress = 100;
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        }, 1000);
      }
    },

    async preloadImages(banners) {
      this.loadedImages = 0;
      this.totalImages = banners.length;

      const promises = banners.map((banner) => {
        return new Promise((resolve) => {
          if (!banner.image) {
            this.loadedImages++;
            this.updateProgress();
            resolve();
            return;
          }

          const img = new Image();
          img.onload = () => {
            this.loadedImages++;
            this.updateProgress();
            resolve();
          };
          img.onerror = () => {
            this.loadedImages++;
            this.updateProgress();
            resolve();
          };
          img.src = banner.image;
        });
      });

      await Promise.all(promises);
      // Forcer la barre à 100% à la toute fin
      this.loadingProgress = 100;
    },

    updateProgress() {
      if (this.totalImages === 0) {
        this.loadingProgress = 100;
      } else {
        this.loadingProgress = Math.round(
            (this.loadedImages / this.totalImages) * 100
        );
      }
    },

    // === GESTION DU STATUT RÉSEAU ===
    updateOnlineStatus() {
      this.isOffline = !navigator.onLine;
    },

    // === GESTION DE LA RÉINITIALISATION DE MOT DE PASSE ===
    toggleResetForm() {
      this.showResetForm = !this.showResetForm;
      // Réinitialiser les erreurs et les champs lors du basculement
      this.resetError = "";
      this.resetSuccess = "";
      this.emailError = "";
      this.resetStep = 1; // Revenir à l'étape 1
      if (!this.showResetForm) {
        this.email = "";
        this.verificationCode = "";
        this.newPassword = "";
        this.confirmPassword = "";
      }
      // Arrêter le timer si actif
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
    },

    clearEmailError() {
      if (this.phoneNumber && this.phoneNumber.trim()) {
        this.emailError = "";
      }
    },

    clearCodeError() {
      if (this.verificationCode.trim()) {
        this.codeError = "";
      }
    },

    clearNewPasswordError() {
      if (this.newPassword) {
        this.newPasswordError = "";
      }
    },

    clearConfirmPasswordError() {
      if (this.confirmPassword) {
        this.confirmPasswordError = "";
      }
    },

    toggleNewPasswordVisibility() {
      this.showNewPassword = !this.showNewPassword;
    },

    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    backToEmailStep() {
      this.resetStep = 1;
      this.resetError = "";
      this.resetSuccess = "";
    },

    startExpirationTimer() {
      // Arrêter tout timer existant
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // Calculer le temps d'expiration en secondes
      this.remainingTime = this.times_expired * 60;

      // Démarrer le timer
      this.timerInterval = setInterval(() => {
        if (this.remainingTime > 0) {
          this.remainingTime--;
        } else {
          // Le lien a expiré
          clearInterval(this.timerInterval);
          this.resetLinkSent = false;
        }
      }, 1000);
    },

    async requestPasswordReset() {
      if (this.resetLoading) {
        return;
      }

      if (!this.phoneNumber || this.phoneNumber.trim() === "") {
        this.emailError = this.$t("login.phone_required");
        return;
      }

      this.resetLoading = true;
      this.resetError = "";

      try {
        let formattedPhone = this.phoneNumber.trim().replace(/\s+/g, "");

        // Normalize phone_prefix to exclude '+'
        let normalizedPhonePrefix = this.phone_prefix.startsWith('+') ? this.phone_prefix.slice(1) : this.phone_prefix;
        let normalizedFormattedPhone = formattedPhone.startsWith('+') ? formattedPhone.slice(1) : formattedPhone;

        if (!normalizedFormattedPhone.startsWith(normalizedPhonePrefix)) {
          formattedPhone = normalizedPhonePrefix + normalizedFormattedPhone;
        } else {
          formattedPhone = normalizedFormattedPhone;
        }

        // Log formattedPhone for debugging
        console.log("Formatted phone for reset:", formattedPhone);

        const response = await ResetPassword(formattedPhone);
        if (response && response.status === 201) {
          this.times_expired = response.data.expires_in_minutes || 30;
          this.resetLinkSent = true;
          this.startExpirationTimer();

          toast.success(this.$t("login.reset_link_sent_success"), {
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Error requesting password reset:", error);
        this.resetError = this.$t("login.reset_error");

        // Afficher une notification toast d'erreur
        toast.error(this.resetError, {
          autoClose: 3000,
        });
      } finally {
        this.resetLoading = false;
      }
    },

    validateResetPasswordForm() {
      let isValid = true;

      // Validation du code de vérification
      if (!this.verificationCode.trim()) {
        this.codeError = this.$t("login.verification_code_required");
        isValid = false;
      } else {
        this.codeError = "";
      }

      // Validation du nouveau mot de passe
      if (!this.newPassword) {
        this.newPasswordError = this.$t("login.new_password_required");
        isValid = false;
      } else if (this.newPassword.length < 8) {
        this.newPasswordError = this.$t("login.password_too_short");
        isValid = false;
      } else {
        this.newPasswordError = "";
      }

      // Validation de la confirmation du mot de passe
      if (!this.confirmPassword) {
        this.confirmPasswordError = this.$t("login.confirm_password_required");
        isValid = false;
      } else if (this.confirmPassword !== this.newPassword) {
        this.confirmPasswordError = this.$t("login.passwords_dont_match");
        isValid = false;
      } else {
        this.confirmPasswordError = "";
      }

      return isValid;
    },
  },
};
</script>


<style scoped>
.input-container {
  position: relative;
}

.input-icon {
  left: 1rem;
  z-index: 10;
}

/* Add padding to the input to make room for the icon */
:deep(.input-container input),
:deep(.password-input-container input) {
  padding-left: 2.5rem !important;
}

/* Adjust the password toggle icon position */
.password-toggle-icon {
  right: 1rem;
  z-index: 10;
  cursor: pointer;
}

/* Styles des onglets avec texte réduit */
.login-tabs {
  margin-bottom: 1.5rem;
}

.tab-buttons {
  display: flex;
  position: relative;
  background: transparent;
  border-bottom: 2px solid #e9ecef;
  padding: 0;
  margin: 0;
}

.tab-button {
  background: transparent;
  border: none;
  padding: 12px 20px;
  font-size: 12px;
  /* Réduit de 15px à 12px */
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  /* Réduit de 0.5px à 0.3px */
  min-height: 50px;
  /* Réduit de 60px à 50px */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* Réduit de 8px à 6px */
}

.tab-button::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, #2dce89, #2dcecc);
  transition: width 0.3s ease;
  border-radius: 2px 2px 0 0;
}

.tab-button:hover {
  color: #2dce89;
  background: rgba(45, 206, 137, 0.05);
}

.tab-button:hover::before {
  width: 100%;
}

.tab-button.active {
  color: #2dce89;
  background: rgba(45, 206, 137, 0.1);
  font-weight: 700;
}

.tab-button.active::before {
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #2dce89, #2dcecc);
  box-shadow: 0 2px 8px rgba(45, 206, 137, 0.4);
}

.tab-button i {
  font-size: 14px;
  /* Réduit de 18px à 14px */
  transition: transform 0.3s ease;
}

.tab-button:hover i {
  transform: scale(1.1);
}

.tab-button.active i {
  transform: scale(1.15);
}

/* Effet de focus pour l'accessibilité */
.tab-button:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(45, 206, 137, 0.3);
}

/* Animation du contenu des onglets */
.tab-content {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767.98px) {
  .login-tabs {
    margin-bottom: 1rem;
  }

  .tab-button {
    padding: 11px 16px;
    font-size: 11px;
    /* Réduit */
    min-height: 47px;
  }

  :deep(.phone-input-container input) {
    padding-left: 6.8rem !important;
    font-size: 15px;
  }
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .phone-prefix {
    left: 2.7rem;
  }

  :deep(.phone-input-container input) {
    padding-left: 6.6rem !important;
  }
}

/* Amélioration de l'indicateur actif */
.tab-buttons::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #e9ecef;
  z-index: 1;
}

.tab-button::before {
  z-index: 2;
}
.fade-slide-enter-active,
.fade-slide-leave-active,
.fade-slide-enter-from,
.fade-slide-leave-to {
  transition: none !important;
  opacity: 1 !important;
  transform: none !important;
}

/* Effet de survol amélioré */
.tab-button:hover {
  transform: translateY(-1px);
}

.tab-button.active {
  transform: translateY(-1px);
}

/* Styles existants continuent... */
form label span {
  color: red;
}

.alert {
  margin-bottom: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.25rem;
}

.alert-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.alert-danger.text-white {
  color: white !important;
}

.password-input-container {
  position: relative;
}

.password-toggle-icon {
  right: 1rem;
  z-index: 10;
  cursor: pointer;
}

/* Ajuster la position de l'input si nécessaire */
:deep(.password-input-container input) {
  padding-right: 2.5rem;
}

.mask {
  background: rgba(0, 0, 0, 0.8);
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}

/* Style pour les champs invalides */
:deep(.is-invalid input) {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: transparent;
}

.slider-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 1s ease-in-out;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Nouveaux styles responsives */
.auth-wrapper {
  max-width: 500px;
  width: 100%;
  padding: 1rem;
}

.logo-img {
  width: 90px;
  height: auto;
  max-width: 100%;
}

.login-card {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
}

/* Media queries */
@media (max-width: 991.98px) {
  .auth-wrapper {
    max-width: 100%;
    padding: 1rem;
  }

  .logo-img {
    width: 100px;
  }

  .title h2 {
    font-size: 1.75rem;
  }
}

@media (max-width: 767.98px) {
  .auth-wrapper {
    padding: 0.5rem;
  }

  .card {
    margin: 1rem;
  }

  .title h2 {
    font-size: 1.5rem;
  }
}

@media (max-width: 575.98px) {
  .logo-img {
    width: 80px;
  }

  .card-body {
    padding: 1rem;
  }
}

:deep(.form-control),
:deep(.input-group) {
  width: 100%;
}

.slider-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 1s ease-in-out;
  overflow: hidden;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.banner-content {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  background: rgba(0, 0, 0, 0.4);
  padding: 2rem;
}

.banner-content .title {
  position: absolute;
  bottom: 10%;
}

.banner-content h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.banner-content p {
  font-size: 1rem;
  margin-bottom: 0;
  opacity: 0.9;
}

.slider-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 1s ease-in-out;
  overflow: hidden;
}

.alert-success {
  background-color: #28a745;
  border-color: #28a745;
}

.alert-success .text-white {
  color: white !important;
}

.progress {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.progress-bar {
  transition: width 1s linear;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #041239;
}

.loading-content {
  text-align: center;
  padding: 2rem;
  max-width: 400px;
}

.progress-container {
  margin-top: 2rem;
}

.progress {
  height: 10px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.progress-bar {
  transition: width 0.3s ease;
  background-color: #2dce89;
}

.logo-title-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-wrapper, .title-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (min-height: 707.98px) {
  .logo-title-container {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    margin-bottom: 20px;
  }



}

@media (max-width: 767.98px) {

  .logo-title-container .logo-img {
    width: 17% !important;
    height: auto;
    max-width: 60px;
    min-width: 40px;
  }

  .title-wrapper {
    transform: none !important;
  }

  .title-wrapper h3 {
    margin: 0;
    font-size: 1.4rem;
  }

  .login-card {
    margin-top: -30px !important;
  }
}
</style>